import { Component, ChangeDetectionStrategy, Input, OnChanges, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import _ from 'lodash';
import { AccessibleObjectsService } from '../../../../generated-sources';

export interface AccessibleObjectOption extends Partial<Omit<AccessibleObjectsService.AccessibleObject, 'type'>> {
    id: AccessibleObjectsService.AccessibleObject['id'];
    label: AccessibleObjectsService.AccessibleObject['label'];
    type?: AccessibleObjectsService.AccessibleObject['type'] | 'APP';
    usable?: boolean;
    usableReason?: string;
}

// Lightweight wrapper around AccessibleObjectOption to please <ng-select>
interface SelectableItem {
    object: AccessibleObjectOption;
    label: string; // Recognized by <ng-select>
    disabled: boolean; // Recognized by <ng-select>
}

function makeSelectableItem(object: AccessibleObjectOption) {
    return {
        object: object,
        label: object.label,
        disabled: object.usable === false
    }
}

@Component({
    selector: 'dss-accessible-objects-selector',
    templateUrl: './accessible-objects-selector.component.html',
    styleUrls: ['./accessible-objects-selector.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: AccessibleObjectsSelectorComponent,
        multi: true
    }]
})
export class AccessibleObjectsSelectorComponent implements ControlValueAccessor, OnChanges {
    @Input() id: string;
    @Input() type: string;
    @Input() multi: boolean;
    @Input() objects: AccessibleObjectOption[] | null;

    selectableItems: SelectableItem[] = [];
    touched = false;
    currentSelection: SelectableItem | SelectableItem[] | undefined;

    constructor(private cd: ChangeDetectorRef) { }

    private onChange?: (objects: AccessibleObjectOption[] | AccessibleObjectOption | undefined) => void;
    private onTouched?: () => void;

    searchFn(term: string, selectableItem: SelectableItem): boolean {
        if (!term) {
            return true;
        }
        const lowerCaseFilter = term.trim().toLowerCase();
        const labelFilter = !!selectableItem.object.label?.toLowerCase().includes(lowerCaseFilter);
        const typeFilter = !!selectableItem.object.type?.toLowerCase().includes(lowerCaseFilter);
        const subtypeFilter = !!selectableItem.object.subtype?.toLowerCase().includes(lowerCaseFilter);

        return labelFilter || typeFilter || subtypeFilter;
    }

    ngOnChanges() {
        this.selectableItems = (this.objects || []).map(makeSelectableItem);
        this.fixupCurrentSelection();
    }

    // Update 'currentSelection' to make sure it keeps references to objects living in 'selectableItems'
    // Note: selected item which are not in 'selectableItems' are preserved
    fixupCurrentSelection() {
        if (this.currentSelection) {
            if (Array.isArray(this.currentSelection)) {
                this.currentSelection = this.currentSelection
                    .map(item => this.selectableItems.find(selectableItem => selectableItem.object === item.object) || item);
            } else {
                const selectedObject = this.currentSelection.object;
                this.currentSelection = this.selectableItems.find(selectableItem => selectableItem.object === selectedObject) || this.currentSelection;
            }
        }
    }

    writeValue(objects: AccessibleObjectOption[] | AccessibleObjectOption | undefined): void {
        if (objects) {
            if (Array.isArray(objects)) {
                this.currentSelection = objects.map(makeSelectableItem);
            } else {
                this.currentSelection = makeSelectableItem(objects);
            }
        } else {
            this.currentSelection = undefined;
        }
        this.fixupCurrentSelection();
        this.cd.markForCheck();
    }

    registerOnChange(onChange: (objects: AccessibleObjectOption[] | AccessibleObjectOption | undefined) => void): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    private markAsTouched(): void {
        if (!this.touched) {
            this.onTouched?.();
            this.touched = true;
        }
    }

    changeCurrentSelection(items: SelectableItem[] | SelectableItem | undefined): void {
        this.currentSelection = items;
        if (Array.isArray(items)) {
            this.onChange?.(items.map(item => item.object));
        } else {
            this.onChange?.(items?.object);
        }
        this.markAsTouched();
    }

    groupByFn(item: SelectableItem): string {
        // Work around https://github.com/ng-select/ng-select/issues/1991 by prefixing group name with a space
        // (the [space] key always opens the ng-select menu and so doesn't trigger https://github.com/ng-select/ng-select/blob/45b61c706bd6d6773574a2fc58eed4340563642f/src/ng-select/lib/ng-select.component.ts#L761)
        return ' ' + (item.object.label.charAt(0) || '').toUpperCase();
    }
}
